import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Visibility, Edit, Search } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setSettings,
  setSelectedSetting,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedSetting,
  updateSetting,
} from '../../store/slices/globalSettingsSlice';
import {
  GET_GLOBAL_SETTINGS_LIST,
  GET_GLOBAL_SETTING,
} from '../../graphql/queries/globalSettings.queries';
import { UPDATE_GLOBAL_SETTING } from '../../graphql/mutations/globalSettings.mutations';
import {
  FindGlobalSettingListInput,
  FindGlobalSettingInput,
  UpdateGlobalSettingInput,
  GlobalSettingsListResponse,
  GlobalSettingResponse,
  UpdateGlobalSettingResponse,
  GlobalSetting,
} from '../../types/globalSettings.types';
import ViewSettingModal from '../../components/GlobalSettings/ViewSettingModal';
import EditSettingModal, { EditSettingData } from '../../components/GlobalSettings/EditSettingModal';
import MainLayout from '../../components/Layout/MainLayout';
import './GlobalSettings.css';

const GlobalSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { settings, loading, error, pagination, search } = useAppSelector(
    (state) => state.globalSettings
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Query para obtener la lista de configuraciones
  const [fetchSettings, { loading: fetchingList }] = useLazyQuery<
    { chatbotGlobalSettings: GlobalSettingsListResponse },
    { params: FindGlobalSettingListInput }
  >(GET_GLOBAL_SETTINGS_LIST, {
    fetchPolicy: 'network-only',
  });

  // Query para obtener una configuración específica
  const [fetchSetting, { loading: fetchingSetting }] = useLazyQuery<
    { chatBotGlobalSetting: GlobalSettingResponse },
    { params: FindGlobalSettingInput }
  >(GET_GLOBAL_SETTING, {
    fetchPolicy: 'network-only',
  });

  // Mutation para actualizar una configuración
  const [updateSettingMutation] = useMutation<
    { updateChatbotGlobalSetting: UpdateGlobalSettingResponse },
    { params: UpdateGlobalSettingInput }
  >(UPDATE_GLOBAL_SETTING);

  // Cargar configuraciones al montar el componente o cuando cambian los parámetros
  useEffect(() => {
    loadSettings();
  }, [pagination.page, pagination.pageSize, search]);

  const loadSettings = async () => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSettings({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.chatbotGlobalSettings?.success && data.chatbotGlobalSettings.data) {
        dispatch(setSettings(data.chatbotGlobalSettings.data));
      } else {
        const errorMessage =
          data?.chatbotGlobalSettings?.message || 'Error al cargar configuraciones';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading settings:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1)); // MUI DataGrid usa 0-based, backend usa 1-based
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSetting({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.chatBotGlobalSetting?.success && data.chatBotGlobalSetting.data) {
        const setting = data.chatBotGlobalSetting.data[0];
        dispatch(setSelectedSetting(setting));
        setViewModalOpen(true);
      } else {
        const errorMessage = data?.chatBotGlobalSetting?.message || 'Error al cargar configuración';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading setting:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchSetting({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.chatBotGlobalSetting?.success && data.chatBotGlobalSetting.data) {
        const setting = data.chatBotGlobalSetting.data[0];
        dispatch(setSelectedSetting(setting));
        setEditModalOpen(true);
      } else {
        const errorMessage = data?.chatBotGlobalSetting?.message || 'Error al cargar configuración';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading setting:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSave = async (data: EditSettingData) => {
    try {
      setSaving(true);
      setSaveError(null);

      const { data: responseData } = await updateSettingMutation({
        variables: {
          params: {
            id: data.id,
            settingKey: data.settingKey,
            settingValue: data.settingValue,
          },
        },
      });

      if (responseData?.updateChatbotGlobalSetting?.success) {
        // Actualización exitosa
        if (responseData.updateChatbotGlobalSetting.data) {
          // Actualizar el registro en Redux
          dispatch(updateSetting(responseData.updateChatbotGlobalSetting.data));
        }
        // Cerrar modal y limpiar
        setEditModalOpen(false);
        dispatch(clearSelectedSetting());
        setSaveError(null);
      } else {
        // Error en la actualización
        const errorMessage =
          responseData?.updateChatbotGlobalSetting?.message || 'Error al actualizar configuración';
        const errors = responseData?.updateChatbotGlobalSetting?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setSaveError(fullErrorMessage);
        // NO cerrar el modal para que el usuario vea el error
      }
    } catch (err: any) {
      console.error('Error updating setting:', err);
      setSaveError(err.message || 'Error de conexión con el servidor');
      // NO cerrar el modal
    } finally {
      setSaving(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedSetting());
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    dispatch(clearSelectedSetting());
    setSaveError(null);
    setSaving(false);
  };

  const columns: GridColDef<GlobalSetting>[] = [
    {
      field: 'settingKey',
      headerName: 'Clave',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'settingValue',
      headerName: 'Valor',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'settingType',
      headerName: 'Tipo',
      width: 100,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 130,
    },
    {
      field: 'createdBy',
      headerName: 'Creado Por',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="global-settings-container">
        <Paper elevation={2} className="global-settings-paper">
        <Box className="global-settings-header">
          <Typography variant="h4" component="h1">
            Configuraciones Globales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administración de configuraciones del sistema
          </Typography>
        </Box>

        <Box className="global-settings-toolbar">
          <Box className="search-box">
            <TextField
              size="small"
              placeholder="Buscar configuración..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ minWidth: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
              sx={{ ml: 1 }}
            >
              Buscar
            </Button>
          </Box>
        </Box>

        {error && (
          <Box className="error-box">
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Box className="data-grid-container">
          <DataGrid
            rows={settings}
            columns={columns}
            loading={loading || fetchingList}
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={{
              page: pagination.page - 1, // DataGrid usa 0-based
              pageSize: pagination.pageSize,
            }}
            onPaginationModelChange={handlePaginationChange}
            paginationMode="server"
            rowCount={pagination.totalRecords}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          />
        </Box>
      </Paper>

        <ViewSettingModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <EditSettingModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
      </div>
    </MainLayout>
  );
};

export default GlobalSettings;
